// AnnualReviewWizard — the 7-step Medicare annual review flow
// described in Medicare Ops Center spec §18. Launched from the
// Medicare Command Center's turning-65 / AWV lists, or from the
// client 360° modal.
//
//   1. New doctors?
//   2. New medications?
//   3. Hospital stays?
//   4. Claims issues?
//   5. Moving?
//   6. Additional protection interest?
//   7. Family / friends needing Medicare guidance?
//
// State persists to client_medical_profile.aep_review_* columns
// (aep_review_status, aep_review_answers, aep_review_completed_at).
// If those columns aren't in the schema yet the write soft-fails
// and the wizard warns — the operator's flow through the questions
// still works.

import React from 'react';
import { supabase } from '../lib/supabase';

const STEPS = [
  {
    key: 'doctors',
    title: 'Any new doctors?',
    prompt: 'Providers added since the last review. Even a specialist visit counts — we\'ll cross-check the plan\'s network next.',
    field: 'new_doctors',
    placeholder: 'e.g. New cardiologist at Dallas Heart — Dr. J. Kim',
  },
  {
    key: 'medications',
    title: 'Any new medications?',
    prompt: 'New scripts, dose changes, or drops. This drives the formulary check before AEP.',
    field: 'new_medications',
    placeholder: 'e.g. Started Eliquis 5mg twice daily · stopped metoprolol',
  },
  {
    key: 'hospital',
    title: 'Any hospital stays this year?',
    prompt: 'Inpatient / observation stays — flags Part A utilization and coverage adequacy.',
    field: 'hospital_stays',
    placeholder: 'e.g. 3-night stay at Baylor in March — cardiac',
  },
  {
    key: 'claims',
    title: 'Any claims issues we should know about?',
    prompt: 'Denials, unexpected balance bills, prior-auth headaches.',
    field: 'claims_issues',
    placeholder: 'e.g. MRI denied — appeal in progress',
  },
  {
    key: 'moving',
    title: 'Any move planned?',
    prompt: 'Moves out of the plan\'s service area open a Special Enrollment Period. County change matters.',
    field: 'moving_plans',
    placeholder: 'e.g. Moving to Collin County in Q3',
  },
  {
    key: 'protection',
    title: 'Interested in additional protection?',
    prompt: 'Dental / vision / hearing / hospital indemnity / final expense — soft ask, not a pitch.',
    field: 'protection_interest',
    placeholder: 'e.g. Interested in dental + hearing — needs cleaning + hearing aid soon',
  },
  {
    key: 'referrals',
    title: 'Family or friends needing Medicare guidance?',
    prompt: 'The single highest-conversion source. Ask casually, capture names + relationship.',
    field: 'referrals',
    placeholder: 'e.g. Sister-in-law Beth turning 65 in October — Denton',
  },
];

export default function AnnualReviewWizard({ clientId, ownerId, clientName, onClose = () => {}, showToast = () => {} }) {
  const [step, setStep] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [profile, setProfile] = React.useState(null);
  const [answers, setAnswers] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    if (!clientId || !ownerId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.from('client_medical_profile')
          .select('primary_doctor, specialists, current_prescriptions, aep_review_answers')
          .eq('owner_id', ownerId).eq('client_id', clientId).maybeSingle();
        if (cancelled) return;
        setProfile(data || null);
        if (data?.aep_review_answers && typeof data.aep_review_answers === 'object') {
          setAnswers(data.aep_review_answers);
        }
      } catch {}
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [clientId, ownerId]);

  const currentStep = STEPS[step];
  const value = answers[currentStep.field] || '';
  const isLast = step === STEPS.length - 1;
  const isFirst = step === 0;

  const setAnswer = (v) => {
    setAnswers(prev => ({ ...prev, [currentStep.field]: v }));
    setDirty(true);
  };

  const persist = async (statusValue) => {
    if (!clientId || !ownerId) return;
    setSaving(true);
    const payload = {
      owner_id: ownerId,
      client_id: clientId,
      aep_review_answers: answers,
      aep_review_status: statusValue,
    };
    if (statusValue === 'completed') payload.aep_review_completed_at = new Date().toISOString();
    try {
      const { error } = await supabase.from('client_medical_profile')
        .upsert(payload, { onConflict: 'owner_id,client_id' });
      if (error) throw error;
      showToast(statusValue === 'completed' ? 'Annual review saved and marked complete.' : 'Progress saved.', 'ok');
      if (statusValue === 'completed') onClose();
    } catch (err) {
      showToast(`Save failed: ${err.message || err}`, 'error');
    } finally { setSaving(false); }
  };

  const next = () => {
    if (isLast) return persist('completed');
    setStep(s => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => setStep(s => Math.max(0, s - 1));

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl max-h-[92vh] overflow-auto shadow-2xl rounded-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-[#24274E] text-white px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.25em] font-bold text-white/60">Medicare annual review</div>
            <div className="text-[20px] font-black" style={{ fontFamily: 'Bebas Neue, system-ui, sans-serif' }}>
              {clientName || 'Client'} · step {step + 1} of {STEPS.length}
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-2xl leading-none">&times;</button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-slate-100">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}/>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {loading ? (
            <div className="text-[13px] text-slate-500 italic">Loading client profile…</div>
          ) : (
            <>
              {/* Contextual snippet from profile */}
              {currentStep.key === 'doctors' && profile?.primary_doctor && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-[12px] text-slate-700">
                  <span className="font-bold uppercase tracking-widest text-[10px] text-slate-500 mr-2">On file</span>
                  Primary: {profile.primary_doctor}
                  {Array.isArray(profile.specialists) && profile.specialists.length > 0 && (
                    <> · Specialists: {profile.specialists.slice(0, 3).map(s => s.name).filter(Boolean).join(', ')}</>
                  )}
                </div>
              )}
              {currentStep.key === 'medications' && Array.isArray(profile?.current_prescriptions) && profile.current_prescriptions.length > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-[12px] text-slate-700">
                  <span className="font-bold uppercase tracking-widest text-[10px] text-slate-500 mr-2">On file</span>
                  {profile.current_prescriptions.slice(0, 4).map(p => p.name).filter(Boolean).join(', ')}
                  {profile.current_prescriptions.length > 4 && ` +${profile.current_prescriptions.length - 4} more`}
                </div>
              )}

              <div>
                <div className="text-[18px] font-black text-slate-900" style={{ fontFamily: 'Bebas Neue, system-ui, sans-serif' }}>
                  {currentStep.title}
                </div>
                <div className="text-[12.5px] text-slate-500 mt-1">{currentStep.prompt}</div>
              </div>

              <textarea
                value={value}
                onChange={e => setAnswer(e.target.value)}
                placeholder={currentStep.placeholder}
                rows={5}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-[13.5px] text-slate-900 focus:border-slate-500 focus:outline-none resize-y"
              />

              {/* Answer summary as we go */}
              {Object.keys(answers).length > 0 && (
                <details className="bg-slate-50 border border-slate-200 rounded-lg">
                  <summary className="px-3 py-2 text-[10.5px] font-bold uppercase tracking-widest text-slate-500 cursor-pointer">
                    Review so far ({Object.values(answers).filter(v => v && v.trim()).length} of {STEPS.length} answered)
                  </summary>
                  <div className="px-3 pb-3 pt-1 space-y-2">
                    {STEPS.map((s, i) => {
                      const a = (answers[s.field] || '').trim();
                      if (!a) return null;
                      return (
                        <div key={s.key} className="text-[12px]">
                          <div className="uppercase tracking-widest text-[9.5px] text-slate-500 font-bold">{s.title}</div>
                          <div className="text-slate-800 whitespace-pre-wrap">{a}</div>
                        </div>
                      );
                    })}
                  </div>
                </details>
              )}
            </>
          )}
        </div>

        {/* Footer controls */}
        <div className="border-t border-slate-100 bg-slate-50 px-6 py-3 flex items-center justify-between">
          <button onClick={back} disabled={isFirst || saving}
            className="text-[12px] font-bold uppercase tracking-wider text-slate-600 hover:text-slate-900 disabled:opacity-40">
            ← Back
          </button>
          <div className="flex items-center gap-2">
            {dirty && (
              <button
                onClick={() => persist('in_progress')}
                disabled={saving}
                className="text-[11px] font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-40"
              >
                {saving ? 'Saving…' : 'Save progress'}
              </button>
            )}
            <button onClick={next} disabled={saving}
              className="text-[12px] font-bold uppercase tracking-wider bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-4 py-2 disabled:opacity-40">
              {isLast ? (saving ? 'Completing…' : 'Complete review →') : 'Next step →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
